import { Check } from "lucide-react";
import { Section, Reveal, Accent } from "@/components/site/Primitives";
import { FaqList } from "@/components/site/FaqList";
import { CtaLink } from "./Hero";

export const HowItWorks = ({ meta, index, site }) => (
  <Section id="how_it_works" index={index} title={meta.title} subtitle={meta.subtitle}>
    <ol className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-[#27272A]">
      {(site.how_it_works || []).map((s, i) => (
        <Reveal key={i} delay={i * 0.08} className="border-b lg:border-b-0 lg:border-r last:border-r-0 border-[#27272A] p-6 md:p-8 pt-10">
          <li className="list-none" data-testid={`how-step-${i}`}>
            <span className="font-display text-6xl font-black text-[#0055FF]/80 leading-none">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-6 text-xl font-bold">{s.title}</h3>
            <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{s.body}</p>
          </li>
        </Reveal>
      ))}
    </ol>
    {site.turnaround?.headline && (
      <div className="mt-12 grid md:grid-cols-12 gap-6 border border-[#0055FF]/40 bg-[#0055FF]/5 p-6 md:p-8" data-testid="turnaround-banner">
        <p className="md:col-span-5 font-display text-2xl font-bold">{site.turnaround.headline}</p>
        <p className="md:col-span-7 text-zinc-400 text-sm leading-relaxed">{site.turnaround.note}</p>
      </div>
    )}
  </Section>
);

export const WhyEditBay = ({ meta, index, site }) => {
  const pts = site.why || [];
  return (
    <Section id="why" index={index} title={meta.title} subtitle={meta.subtitle}>
      <div className="grid md:grid-cols-6 gap-4">
        {pts.map((p, i) => (
          <Reveal key={i} delay={i * 0.06} className={i % 3 === 0 ? "md:col-span-4" : "md:col-span-2"}>
            <div className="h-full bg-[#121212] border border-[#27272A] p-8 md:p-10 hover:border-[#0055FF]/50 transition-colors" data-testid={`why-point-${i}`}>
              <h3 className="text-xl md:text-2xl font-bold">{p.title}</h3>
              <p className="mt-3 text-zinc-400 leading-relaxed">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
};

export const AgencySection = ({ meta, index, site }) => {
  const a = site.agency || {};
  return (
    <Section id="agency" index={index} className="bg-[#0a0a0a] border-y border-[#27272A]">
      <div className="grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-6">
          <p className="eb-label mb-4">{meta.title}</p>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">{a.title}</h2>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-xl">{a.body}</p>
          <div className="mt-10"><CtaLink cta={a.cta} primary testid="agency-cta" /></div>
        </Reveal>
        <ul className="lg:col-span-6 border-t border-[#27272A]">
          {(a.points || []).map((p, i) => (
            <li key={i} className="flex items-center gap-4 py-6 border-b border-[#27272A] text-lg" data-testid={`agency-point-${i}`}>
              <Check className="text-[#0055FF] shrink-0" size={20} />{p}
            </li>
          ))}
        </ul>
      </div>
    </Section>
  );
};

export const FaqSection = ({ meta, index, faqs }) => {
  const list = faqs.filter((f) => f.show_on_homepage);
  if (!list.length) return null;
  return (
    <Section id="faqs" index={index}>
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4"><h2 className="text-3xl md:text-5xl font-bold tracking-tight">{meta.title}</h2>{meta.subtitle && <p className="mt-4 text-zinc-400">{meta.subtitle}</p>}</div>
        <div className="lg:col-span-8"><FaqList faqs={list} /></div>
      </div>
    </Section>
  );
};

export const CtaSection = ({ site }) => {
  const c = site.cta || {};
  return (
    <section className="py-24 md:py-36 relative overflow-hidden" data-testid="section-cta">
      <div className="absolute -right-40 top-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#0055FF]/20 blur-[140px] pointer-events-none" />
      <div className="eb-container relative">
        <h2 className="text-5xl md:text-7xl lg:text-8xl font-semibold tracking-[-0.045em] leading-[0.95] max-w-5xl"><Accent text={c.headline} /></h2>
        <p className="mt-8 text-zinc-400 text-lg max-w-xl">{c.body}</p>
        <div className="mt-10"><CtaLink cta={{ label: c.label, href: c.href }} primary testid="final-cta" /></div>
      </div>
    </section>
  );
};
